import { z } from "zod";

import { resolveAltaContactosRouting } from "./contact-routing";

type RpaValue = string | number | boolean | null | undefined;

type AltaContactoNet4Payload = Record<string, RpaValue> & {
  net4_sync_requested?: RpaValue;
  net4_database?: RpaValue;
  net4_client_id?: RpaValue;
  net4_client_cif?: RpaValue;
  net4_center_id?: RpaValue;
  net4_center_order?: RpaValue;
  net4_contact_order?: RpaValue;
  net4_contact_order_reservation_key?: RpaValue;
  net4_contact_reservation_consumed?: RpaValue;
  net4_contact_existing?: RpaValue;
  net4_contact_id?: RpaValue;
  hubspot_existing_contact_id?: RpaValue;
  contact_id?: RpaValue;
  firstname?: RpaValue;
  lastname?: RpaValue;
  email?: RpaValue;
  phone?: RpaValue;
  cargo_en_la_empresa?: RpaValue;
  cargo_en_la_empresa_label?: RpaValue;
  firmante_cpd?: RpaValue;
  id_cliente?: RpaValue;
};

const rpaValueSchema = z.union([z.string(), z.number(), z.boolean()]);

const text = (value: RpaValue): string => {
  const parsed = rpaValueSchema.safeParse(value);
  return parsed.success ? String(parsed.data).trim() : "";
};

const isSyncRequested = (value: RpaValue): boolean => text(value).toUpperCase() === "S";
const isEnabled = (value: RpaValue): boolean => ["TRUE", "S"].includes(text(value).toUpperCase());

export function normalizeAltaContactoForRpa<T extends AltaContactoNet4Payload>(data: T): T {
  const syncRequested = isSyncRequested(data.net4_sync_requested);
  const reservationConsumed = isEnabled(data.net4_contact_reservation_consumed);
  const net4ContactExisting = isEnabled(data.net4_contact_existing);
  const database = text(data.net4_database).toLowerCase();
  const routingPlan = resolveAltaContactosRouting({
    hubspotContact:
      text(data.hubspot_existing_contact_id) || text(data.contact_id) ? "existing" : "new",
    hasNet4Client: syncRequested
      ? Boolean(database && text(data.net4_client_id) && text(data.net4_client_cif))
      : false,
    net4Contact: syncRequested
      ? data.net4_contact_existing === "S"
        ? "existing"
        : data.net4_contact_existing === ""
          ? "unknown"
          : "missing"
    : "unknown",
  });

  const hasVerifiedNet4Client = Boolean(
    syncRequested && database && text(data.net4_client_id) && text(data.net4_client_cif),
  );

  if (syncRequested && routingPlan.status === "checking") {
    return {
      ...data,
      net4_sync_requested: "S",
      crear_centro: "N",
      crear_contacto: "N",
    };
  }

  if (hasVerifiedNet4Client && net4ContactExisting && !routingPlan.syncNet4) {
    return {
      ...data,
      net4_sync_requested: "N",
      crear_centro: "N",
      crear_contacto: "N",
      net4_contact_existing: "S",
      net4_contact_id: text(data.net4_contact_id),
    };
  }

  if (!syncRequested || !routingPlan.syncNet4) {
    return {
      ...data,
      net4_sync_requested: syncRequested ? "S" : "N",
      crear_centro: "N",
      crear_contacto: "N",
      familia_de_productos: "",
      id_cliente: "",
      cif: "",
      numero_centro: "",
      orden_contacto: "",
      nombre_contacto: "",
      cargo_contacto: "",
      telefono_contacto: "",
      email_contacto: "",
      firma_contacto: "",
      net4_contact_existing: "N",
      net4_contact_id: "",
    };
  }

  const fullName = [text(data.firstname), text(data.lastname)].filter(Boolean).join(" ");

  return {
    ...data,
    net4_sync_requested: "S",
    familia_de_productos: database === "ett" ? "ETT" : "NO",
    crear_centro: "N",
    crear_contacto:
      routingPlan.createNet4Contact && !net4ContactExisting && !reservationConsumed ? "S" : "N",
    id_cliente: text(data.net4_client_id),
    cif: text(data.net4_client_cif),
    numero_centro: text(data.net4_center_id),
    orden_contacto: text(data.net4_contact_order),
    nombre_contacto: fullName,
    cargo_contacto:
      text(data.cargo_en_la_empresa_label) || text(data.cargo_en_la_empresa),
    telefono_contacto: text(data.phone),
    email_contacto: text(data.email),
    net4_contact_existing: net4ContactExisting ? "S" : "N",
    net4_contact_id: text(data.net4_contact_id),
    firma_contacto:
      database === "ett" ? (isEnabled(data.firmante_cpd) ? "S" : "N") : "",
  };
}
